import { useCallback, useEffect, useState } from "react";
import * as api from "./api";

interface AsyncState<T> {
  data: T | null;
  error: string | null;
  loading: boolean;
  reload: () => void;
}

function errMessage(e: unknown): string {
  return e instanceof api.ApiError ? e.message : "Couldn't reach the server";
}

export function useConnections(tenant: string | undefined): AsyncState<api.Connection[]> {
  const [data, setData] = useState<api.Connection[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(() => {
    if (!tenant) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    api
      .listConnections(tenant)
      .then((d) => setData(d))
      .catch((e) => setError(errMessage(e)))
      .finally(() => setLoading(false));
  }, [tenant]);

  useEffect(() => reload(), [reload]);
  return { data, error, loading, reload };
}

export function useConnectionConfig(
  tenant: string | undefined,
  slug: string | undefined,
): AsyncState<api.ConnectionConfig> {
  const [data, setData] = useState<api.ConnectionConfig | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(() => {
    if (!tenant || !slug) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    api
      .getConnectionConfig(tenant, slug)
      .then((d) => setData(d))
      .catch((e) => setError(errMessage(e)))
      .finally(() => setLoading(false));
  }, [tenant, slug]);

  useEffect(() => reload(), [reload]);
  return { data, error, loading, reload };
}

export function useExchange(): AsyncState<api.ExchangeListing[]> {
  const [data, setData] = useState<api.ExchangeListing[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(() => {
    setLoading(true);
    setError(null);
    api
      .listExchange()
      .then((d) => setData(d))
      .catch((e) => setError(errMessage(e)))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => reload(), [reload]);
  return { data, error, loading, reload };
}

export function useConnection(
  tenant: string | undefined,
  slug: string | undefined,
): AsyncState<api.Connection> {
  const [data, setData] = useState<api.Connection | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(() => {
    if (!tenant || !slug) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    api
      .getConnection(tenant, slug)
      .then((d) => setData(d))
      .catch((e) => setError(errMessage(e)))
      .finally(() => setLoading(false));
  }, [tenant, slug]);

  useEffect(() => reload(), [reload]);
  return { data, error, loading, reload };
}
